import React, { useEffect, useRef, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { profile, social, themeSwatches } from './data';
import Home from './pages/Home';
import About from './pages/About';
import Projects from './pages/Projects';
import Apps from './pages/Apps';
import Certifications from './pages/Certifications';
import Contact from './pages/Contact';
import './modernist.css';
import './pages.css';

/**
 * Modernist presentation.
 *
 * The design is a single component driving a `page` state value; here that maps
 * onto the routes the site already serves, so deep links and the back button
 * keep working and the URL still means something:
 *
 *   home -> /      about -> /about       projects -> /projects
 *   apps -> /myapps  certs -> /certifications  contact -> /contact
 */
const NAV = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'about', label: 'About', path: '/about' },
  { id: 'projects', label: 'Projects', path: '/projects' },
  { id: 'apps', label: 'My Apps', path: '/myapps' },
  { id: 'certs', label: 'Certifications', path: '/certifications' },
  { id: 'contact', label: 'Contact', path: '/contact' },
];

const Nav = ({ theme, onTheme, openThemeModal }) => {
  const { pathname } = useLocation();

  return (
    <nav className="m-nav" aria-label="Primary">
      <Link to="/" className="m-nav__brand">
        <span className="m-nav__mark" aria-hidden="true" />
        <span className="m-nav__name">{profile.name}</span>
      </Link>

      <div className="m-nav__links">
        {NAV.map((item) => (
          <Link
            key={item.id}
            to={item.path}
            className={`m-nav__link${pathname === item.path ? ' is-active' : ''}`}
            aria-current={pathname === item.path ? 'page' : undefined}
          >
            {item.label}
          </Link>
        ))}
      </div>

      {/* The design's four ground swatches. These switch the site theme, so
          they go through the app's own changeTheme rather than local state —
          otherwise the picker and the swatches would disagree. */}
      <div className="m-nav__swatches" role="group" aria-label="Modernist variant">
        {themeSwatches.map((sw) => (
          <button
            key={sw.id}
            type="button"
            title={sw.name}
            aria-label={`${sw.name} variant`}
            aria-pressed={theme === sw.id}
            className={`m-swatch${theme === sw.id ? ' is-active' : ''}`}
            style={{ background: sw.ground, borderColor: sw.accent }}
            onClick={() => onTheme(sw.id)}
          />
        ))}
      </div>

      {/* Way back out to the full picker: the swatches only cover the four
          Modernist grounds, and without this the other themes would be
          unreachable while this presentation is active. */}
      <button type="button" className="m-nav__themes" onClick={openThemeModal}>
        Themes
      </button>

    </nav>
  );
};

const Footer = () => (
  <footer className="m-footer">
    <div>
      <div className="m-eyebrow">Open to work</div>
      <p style={{ margin: '10px 0 0', fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        © {new Date().getFullYear()} {profile.name} — {profile.location}
      </p>
    </div>
    <div className="m-footer__socials">
      {social.map((s) => (
        <a
          key={s.label}
          className="m-footer__social"
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {s.label}
        </a>
      ))}
    </div>
  </footer>
);

/**
 * Page flip.
 *
 * The design plays a short flip on navigation before swapping content. Here the
 * route has already changed by the time we render, so the effect runs on
 * pathname change: mount flipped, then settle on the next frame.
 */
/**
 * Page transition.
 *
 * The animated element is keyed on the pathname, so each navigation mounts a
 * new node instead of reusing one.
 *
 * That is the whole trick. A reused node transitions from whatever opacity it
 * currently has, and the flipped state only survives a frame or two before
 * settling — so the page dipped to roughly 0.9 and came straight back. The
 * transition was running; there was just nothing to see. A freshly mounted
 * node genuinely starts at opacity 0, so the settle has the full distance to
 * travel.
 */
const PageFlip = ({ children }) => {
  const { pathname } = useLocation();
  return <FlipIn key={pathname}>{children}</FlipIn>;
};

const FlipIn = ({ children }) => {
  // Mounts flipped, settles on the next frame.
  const [settled, setSettled] = useState(false);
  const frame = useRef(0);
  const timer = useRef(0);

  useEffect(() => {
    // One frame so the flipped state is actually painted; the transition then
    // has a real starting point rather than an instant class swap.
    frame.current = requestAnimationFrame(() => setSettled(true));
    /*
       requestAnimationFrame does not fire while the tab is hidden, and a page
       that never settles sits at opacity 0 with nothing on screen. This is the
       guard against that — the same failure this component had on first load
       before it was keyed.
    */
    timer.current = window.setTimeout(() => setSettled(true), 150);

    return () => {
      cancelAnimationFrame(frame.current);
      clearTimeout(timer.current);
    };
  }, []);

  return (
    <div className={`m-page ${settled ? 'is-settled' : 'is-flipping'}`}>{children}</div>
  );
};

/**
 * Archivo.
 *
 * The design pulls this from Google Fonts; the site's index.html only carries
 * Orbitron and Exo 2 for the other themes. Injecting it here rather than adding
 * it to index.html keeps a font request off every non-Modernist theme, and the
 * link is left in place once added so re-entering the theme is instant.
 */
const useArchivo = () => {
  useEffect(() => {
    const id = 'm-font-archivo';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&display=swap';
    document.head.appendChild(link);
  }, []);
};

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);
  return null;
};

const ModernistSite = ({ theme, onTheme, openThemeModal }) => {
  useArchivo();

  return (
  <div className="m-root" data-modernist="">
    <ScrollTop />
    <Nav theme={theme} onTheme={onTheme} openThemeModal={openThemeModal} />

    <main id="m-main">
      <PageFlip>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/myapps" element={<Apps />} />
          <Route path="/certifications" element={<Certifications />} />
          <Route path="/contact" element={<Contact />} />
          {/* Anything else — including the retired /hobbies — lands on Home
              rather than a dead end, since this presentation has no 404 page
              of its own in the design. */}
          <Route path="*" element={<Home />} />
        </Routes>
      </PageFlip>
    </main>

    <Footer />
  </div>
  );
};

export default ModernistSite;
